import { useTranslation } from 'react-i18next'
import type { InvestigationReadLead } from '@/domains/types'
import { MarkdownView } from '@/ui/components/common/MarkdownView'
import { CollapsibleSection } from '@/ui/components/common/CollapsibleSection'
import { ReadFactBlock } from './ReadFactBlock'
import { hasContent } from './hasContent'

/**
 * One lead as a section of the article: a plain heading (also the table of
 * contents' anchor target), the description, then its facts in order. Only
 * the lead's original source and its analyses are disclosures, collapsed by
 * default — the narrative itself is never hidden behind a toggle.
 */
export function ReadLeadSection({ entry }: { entry: InvestigationReadLead }) {
  const { t } = useTranslation()
  const { lead, source, facts } = entry
  const sourceDescription = source?.description
  const hasSource = !!source && (hasContent(sourceDescription) || !!source.url)

  return (
    <section id={`read-lead-${lead.id}`} className="read-lead-section scroll-mt-24 mb-10">
      {/* `mb-10` mirrors the `mb-10` section gap that sits above this title,
          so a lead heading is spaced identically top and bottom. */}
      <h2 className="text-3xl sm:text-4xl font-display font-bold text-foreground mb-10">
        {lead.title}
      </h2>

      {lead.imageUrl && (
        <figure className="mb-8">
          {/* `aspect-[21/9]` reserves the image's box before it loads, so a
              TOC anchor landing below it isn't thrown off by layout shift
              once the image arrives. */}
          <div className="rounded-lg overflow-hidden aspect-[21/9]">
            <img src={lead.imageUrl} alt={lead.title} className="w-full h-full object-cover" />
          </div>
        </figure>
      )}

      {hasContent(lead.description) && (
        <MarkdownView content={lead.description} variant="article" />
      )}

      {hasSource && (
        <CollapsibleSection title={t('read_mode.source_label')} className="mt-4">
          <blockquote className="border-l-2 border-primary/50 pl-4 text-base text-muted-foreground">
            {hasContent(sourceDescription) && (
              <MarkdownView content={sourceDescription ?? ''} variant="article" />
            )}
            {source.url && (
              <a
                href={source.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-primary hover:underline text-wrap-anywhere"
              >
                {source.url}
              </a>
            )}
          </blockquote>
        </CollapsibleSection>
      )}

      {hasContent(lead.notes) && (
        <CollapsibleSection title={t('read_mode.notes_label')} className="mt-2">
          <MarkdownView content={lead.notes ?? ''} variant="article" />
        </CollapsibleSection>
      )}

      {facts.length > 0 ? (
        <div className="mt-6">
          {facts.map((fact) => (
            <ReadFactBlock key={fact.id} fact={fact} />
          ))}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground mt-4">{t('read_mode.no_facts')}</p>
      )}
    </section>
  )
}
